import { ConsoleLogEntry, FEEDBACK_CONSOLE_LOGS_MAX } from '../../models/feedback.js'

const buffer: ConsoleLogEntry[] = []
let initialized = false

const stringifyArg = (arg: unknown): string => {
    if (typeof arg === 'string') return arg
    if (arg instanceof Error) return arg.stack ?? arg.message
    try {
        return JSON.stringify(arg)
    } catch {
        return String(arg)
    }
}

const truncate = (text: string, max: number): string =>
    text.length > max ? text.slice(0, max - 1) + '…' : text

const push = (entry: ConsoleLogEntry) => {
    buffer.push(entry)
    if (buffer.length > FEEDBACK_CONSOLE_LOGS_MAX) {
        buffer.splice(0, buffer.length - FEEDBACK_CONSOLE_LOGS_MAX)
    }
}

const recordConsole = (level: 'error' | 'warn', args: unknown[]) => {
    const message = truncate(args.map(stringifyArg).join(' '), 4000)
    const errorArg = args.find((a): a is Error => a instanceof Error)
    push({
        level,
        message,
        timestamp: new Date().toISOString(),
        ...(errorArg?.stack ? { stack: truncate(errorArg.stack, 8000) } : {}),
    })
}

export const initConsoleBuffer = () => {
    if (initialized) return
    initialized = true

    const originalError = console.error
    console.error = (...args: unknown[]) => {
        recordConsole('error', args)
        originalError.apply(console, args as never[])
    }

    const originalWarn = console.warn
    console.warn = (...args: unknown[]) => {
        recordConsole('warn', args)
        originalWarn.apply(console, args as never[])
    }

    window.addEventListener('error', (event: ErrorEvent) => {
        push({
            level: 'unhandled',
            message: truncate(event.message || String(event.error ?? 'Unknown error'), 4000),
            timestamp: new Date().toISOString(),
            ...(event.error instanceof Error && event.error.stack
                ? { stack: truncate(event.error.stack, 8000) }
                : {}),
        })
    })

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason as unknown
        push({
            level: 'rejection',
            message: truncate(stringifyArg(reason), 4000),
            timestamp: new Date().toISOString(),
            ...(reason instanceof Error && reason.stack
                ? { stack: truncate(reason.stack, 8000) }
                : {}),
        })
    })
}

export const getRecentLogs = (): ConsoleLogEntry[] => [...buffer]
